import { AvatarUploadField } from '@/components/creve/AvatarUploadField';
import { LandingFooter } from '@/components/creve/LandingFooter';
import { LandingHeader } from '@/components/creve/LandingHeader';
import { SocialLinksEditor } from '@/components/creve/SocialLinksEditor';
import { VerifiedBadge } from '@/components/creve/VerifiedBadge';
import { PageLayout } from '@/components/poof-ui';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import {
  setVendors,
  subscribeVendors,
  updateVendors,
  VendorsRequest,
  VendorsResponse,
} from '@/lib/collections/vendors';
import { Address, Time } from '@/lib/db-client';
import { useAuth } from '@pooflabs/web';
import { Lock, Save, UserRound } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

const LEVEL_OPTIONS = ['100', '200', '300', '400', '500'];

export const ProfileEditPage: React.FC = () => {
  const { user, login } = useAuth();

  const { data: vendor, loading: vendorLoading } = useRealtimeData<VendorsResponse | null>(
    subscribeVendors,
    !!user?.address,
    user?.address
  );

  const initialized = useRef(false);
  const [fullName, setFullName] = useState('');
  const [bio, setBio] = useState('');
  const [department, setDepartment] = useState('');
  const [level, setLevel] = useState('');
  const [phone, setPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [avatar, setAvatar] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialized.current || vendorLoading) return;
    if (vendor) {
      setFullName(vendor.fullName ?? '');
      setBio(vendor.bio ?? '');
      setDepartment(vendor.department ?? '');
      setLevel(vendor.level ?? '');
      setPhone(vendor.phone ?? '');
      setContactEmail(vendor.contactEmail ?? '');
      setAvatar(vendor.avatar ?? '');
    }
    initialized.current = true;
  }, [vendor, vendorLoading]);

  const isVerified = vendor?.verified === true;

  const handleLogin = async () => {
    try {
      await login();
    } catch (error) {
      console.error('Failed to login', error);
      toast.error('Could not open wallet login. Please try again.');
    }
  };

  const handleSave = async () => {
    if (!user?.address) return;
    if (!fullName.trim()) {
      toast.error('Full name is required.');
      return;
    }

    setSaving(true);
    try {
      let success: boolean;

      if (vendor) {
        const updatePayload: Partial<VendorsRequest> = {
          bio: bio || undefined,
          department: department || undefined,
          level: level || undefined,
          phone: phone || undefined,
          contactEmail: contactEmail || undefined,
          avatar: avatar || undefined,
          updatedAt: Time.Now,
        };
        // fullName is locked once verified — never send a changed value, and
        // never send a changed `verified` (owners can't self-verify).
        if (!isVerified) {
          updatePayload.fullName = fullName.trim();
        }
        success = await updateVendors(user.address, updatePayload);
      } else {
        success = await setVendors(user.address, {
          owner: Address.publicKey(user.address),
          fullName: fullName.trim(),
          bio: bio || undefined,
          department: department || undefined,
          level: level || undefined,
          phone: phone || undefined,
          contactEmail: contactEmail || undefined,
          avatar: avatar || undefined,
          verified: false,
          createdAt: Time.Now,
          updatedAt: Time.Now,
        });
      }

      if (success) {
        toast.success('Profile saved.');
      } else {
        toast.error('Could not save your profile. Please try again.');
      }
    } catch (error) {
      console.error('Failed to save vendor profile', error);
      toast.error('Something went wrong while saving.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout fullBleed footer={<LandingFooter />}>
      <LandingHeader />
      <div className='container py-10 sm:py-14'>
        <div className='mx-auto max-w-2xl'>
          <div className='mb-8 flex items-center justify-between gap-3'>
            <div className='flex items-center gap-3'>
              <span className='flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-primary'>
                <UserRound className='h-5 w-5' />
              </span>
              <div>
                <h1 className='font-heading text-2xl font-bold text-foreground sm:text-3xl'>
                  Edit profile
                </h1>
                <p className='text-sm text-muted-foreground'>
                  This is what buyers see on your vendor listing.
                </p>
              </div>
            </div>
            {vendor && <VerifiedBadge verified={isVerified} />}
          </div>

          {!user ? (
            <div className='rounded-2xl border border-border bg-card p-10 text-center'>
              <p className='text-sm text-muted-foreground'>
                Log in with your wallet to edit your vendor profile.
              </p>
              <Button
                onClick={handleLogin}
                className='mt-5 rounded-full bg-primary px-6 text-primary-foreground hover:bg-primary/90'
              >
                Log in
              </Button>
            </div>
          ) : (
            <div className='space-y-6 rounded-2xl border border-border bg-card p-6 sm:p-8'>
              <AvatarUploadField
                fileId={`vendor-avatar-${user.address}`}
                value={avatar}
                onUploaded={setAvatar}
              />

              <div>
                <Label htmlFor='fullName' className='text-sm font-semibold text-foreground'>
                  Full name
                </Label>
                {isVerified ? (
                  <div className='mt-1.5 flex items-center gap-2 rounded-xl border border-border bg-muted px-3 py-2.5'>
                    <Lock className='h-4 w-4 shrink-0 text-muted-foreground' />
                    <span className='text-sm text-foreground'>{fullName}</span>
                  </div>
                ) : (
                  <Input
                    id='fullName'
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder='Your full name'
                    className='mt-1.5 rounded-xl'
                  />
                )}
                {isVerified && (
                  <p className='mt-1.5 text-xs text-muted-foreground'>
                    Your name is locked because your identity is verified.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor='bio' className='text-sm font-semibold text-foreground'>
                  Bio
                </Label>
                <Textarea
                  id='bio'
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder='Tell buyers what you sell and why they can trust you.'
                  className='mt-1.5 rounded-xl'
                  rows={4}
                />
              </div>

              <div className='grid gap-5 sm:grid-cols-2'>
                <div>
                  <Label htmlFor='department' className='text-sm font-semibold text-foreground'>
                    Department
                  </Label>
                  <Input
                    id='department'
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    placeholder='e.g. Computer Science'
                    className='mt-1.5 rounded-xl'
                  />
                </div>

                <div>
                  <Label htmlFor='level' className='text-sm font-semibold text-foreground'>
                    Level
                  </Label>
                  <Select value={level} onValueChange={setLevel}>
                    <SelectTrigger id='level' className='mt-1.5 rounded-xl'>
                      <SelectValue placeholder='Select level' />
                    </SelectTrigger>
                    <SelectContent>
                      {LEVEL_OPTIONS.map((lvl) => (
                        <SelectItem key={lvl} value={lvl}>
                          {lvl} level
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className='grid gap-5 sm:grid-cols-2'>
                <div>
                  <Label htmlFor='phone' className='text-sm font-semibold text-foreground'>
                    Phone
                  </Label>
                  <Input
                    id='phone'
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder='080...'
                    className='mt-1.5 rounded-xl'
                  />
                </div>

                <div>
                  <Label htmlFor='contactEmail' className='text-sm font-semibold text-foreground'>
                    Contact email
                  </Label>
                  <Input
                    id='contactEmail'
                    type='email'
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder='For buyers to reach you'
                    className='mt-1.5 rounded-xl'
                  />
                  <p className='mt-1 text-xs text-muted-foreground'>
                    Public contact email — separate from your login email.
                  </p>
                </div>
              </div>

              <div className='border-t border-border pt-5'>
                <SocialLinksEditor vendorId={user.address} />
              </div>

              <Button
                onClick={handleSave}
                disabled={saving}
                size='lg'
                className='w-full gap-2 rounded-full bg-primary text-base text-primary-foreground hover:bg-primary/90 disabled:opacity-70'
              >
                <Save className='h-4 w-4' />
                {saving ? 'Saving...' : 'Save profile'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default ProfileEditPage;
